// Apolo IDE — useSidebarPins (Pedro 2026-06-25, sprint cursor-strip).
//
// Pin local de sessões na sidebar (LS-backed, sem migration D1).
// API:
//   const { pinned, isPinned, togglePin } = useSidebarPins();
// LS key: `apolo:sidebar:pinned-sessions` → JSON array de session IDs.

import { useCallback, useEffect, useState } from 'react';

export const LS_PINS = 'apolo:sidebar:pinned-sessions';
const EVENT = 'apolo:sidebar:pinned-sessions:change';

function readPins() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LS_PINS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writePins(ids) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_PINS, JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { ids } }));
  } catch {
    /* best-effort: full LS quota or private mode. UI still works in-memory. */
  }
}

export function useSidebarPins() {
  const [pinned, setPinned] = useState(readPins);

  // Listen for cross-component pin changes (e.g. unpin from one row updates
  // the badge counter in another). Same-tab signal via CustomEvent.
  useEffect(() => {
    function onChange(e) {
      if (Array.isArray(e?.detail?.ids)) setPinned(e.detail.ids);
    }
    function onStorage(e) {
      if (e.key === LS_PINS) setPinned(readPins());
    }
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const isPinned = useCallback((id) => pinned.includes(id), [pinned]);

  const togglePin = useCallback((id) => {
    if (!id) return;
    setPinned((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      writePins(next);
      return next;
    });
  }, []);

  // Explicit unpin (idempotent) — used by drag-out-of-Pinned. Only touches LS
  // pins; a no-op when the id isn't pinned.
  const unpin = useCallback((id) => {
    if (!id) return;
    setPinned((prev) => {
      if (!prev.includes(id)) return prev;
      const next = prev.filter((x) => x !== id);
      writePins(next);
      return next;
    });
  }, []);

  return { pinned, isPinned, togglePin, unpin };
}

export default useSidebarPins;
